// components/const/Daily/MaterialTable.jsx
import React, { useState, useEffect, useRef } from "react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useParams } from "react-router-dom";
import {
  getWorkReportDetail,
  getMaterialUsage,
  getMaterials,
} from "../../../api/const";
import MaterialNameInput from "./MaterialNameInput";

function SortableMaterialRow({
  rowId,
  item,
  idx,
  total,
  allMaterials,
  handleChange,
  handleSelect,
  deleteRow,
}) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: rowId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className="hover:bg-gray-50 border-b border-black last:border-b-0"
    >
      {/* 드래그 핸들 */}
      <td className="px-2 py-1 text-center align-middle border-r border-black bg-gray-50">
        <button
          type="button"
          {...attributes}
          {...listeners}
          className="cursor-grab px-1 py-0.5 border rounded bg-white active:cursor-grabbing"
          title="드래그해서 순서 변경"
        >
          ::
        </button>
      </td>

      {/* 자재명 */}
      <td className="px-2 py-1 border-r border-black">
        <MaterialNameInput
          value={item.name}
          options={allMaterials}
          onInputChange={(val) => handleChange(idx, "name", val)}
          onSelect={(opt) => handleSelect(idx, opt)}
        />
      </td>

      <td className="px-2 py-1 border-r border-black">
        <input
          type="text"
          value={item.spec}
          onChange={(e) => handleChange(idx, "spec", e.target.value)}
          className="w-full rounded border px-2 py-1 text-sm"
        />
      </td>

      <td className="px-2 py-1 border-r border-black">
        <input
          type="text"
          value={item.unit}
          onChange={(e) => handleChange(idx, "unit", e.target.value)}
          className="w-full rounded border px-2 py-1 text-sm"
        />
      </td>

      <td className="px-2 py-1 text-center border-r border-black">{item.prev_total}</td>

      <td className="px-2 py-1 border-r border-black">
        <input
          type="number"
          value={item.today}
          onChange={(e) =>
            handleChange(idx, "today", Number(e.target.value) || 0)
          }
          className="w-full rounded border px-2 py-1 text-sm"
        />
      </td>

      <td className="px-2 py-1 text-center border-r border-black">{total}</td>

      <td className="px-2 py-1 text-center border-black">
        <button onClick={() => deleteRow(idx)} className="doc-del">
          삭제
        </button>
      </td>
    </tr>
  );
}

export default function MaterialTable({ dailyId, date, onDataChange }) {
  const { id: project } = useParams();
  const [data, setData] = useState([
    {
      id: null,
      name: "",
      spec: "",
      unit: "",
      prev_total: 0,
      today: 0,
    },
  ]); // ⭐ 기본 1행 추가
  const [allMaterials, setAllMaterials] = useState([]);
  const wrapperRef = useRef();

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const getRowId = (row, index) => (row.id != null ? row.id : `row-${index}`);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = data.findIndex((row, i) => getRowId(row, i) === active.id);
    const newIndex = data.findIndex((row, i) => getRowId(row, i) === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const newData = arrayMove(data, oldIndex, newIndex);
    setData(newData);
    onDataChange(newData);
  };

  // 전체 자재 목록
  useEffect(() => {
    getMaterials().then(setAllMaterials).catch(console.error);
  }, []);

  // 기존 데이터 로드
  useEffect(() => {
    if (!dailyId || !project || !date) return;

    (async () => {
      try {
        const report = await getWorkReportDetail(dailyId);

        if (!report.materials?.length) return;

        const withPrev = await Promise.all(
          report.materials.map(async (m) => {
            const usage = await getMaterialUsage(project, m.id, null, date);

            const sum = usage
              .filter((u) => new Date(u.date) < new Date(date))
              .reduce((acc, u) => acc + (u.qty || 0), 0);

            return {
              id: m.id,
              name: m.name,
              spec: m.spec,
              unit: m.unit,
              prev_total: sum,
              today: m.qty,
            };
          })
        );

        setData(withPrev);
        onDataChange(withPrev);
      } catch (err) {
        console.error("자재 로드 실패:", err);
      }
    })();
  }, [dailyId, project, date, onDataChange]);

  const updateRow = (idx, changes) => {
    setData((prev) => {
      const updated = prev.map((row, i) =>
        i === idx ? { ...row, ...changes } : row
      );
      onDataChange(updated);
      return updated;
    });
  };

  const handleSelect = async (idx, opt) => {
    updateRow(idx, {
      id: opt.id,
      name: opt.name,
      spec: opt.spec,
      unit: opt.unit,
    });

    const usage = await getMaterialUsage(project, opt.id, null, date);
    const sum = usage.reduce((acc, u) => acc + u.qty, 0);

    updateRow(idx, { prev_total: sum });
  };

  const handleChange = (idx, field, value) => {
    updateRow(idx, { [field]: value });
  };

  const addRow = () => {
    const newRow = {
      id: null,
      name: "",
      spec: "",
      unit: "",
      prev_total: 0,
      today: 0,
    };
    const updated = [...data, newRow];
    setData(updated);
    onDataChange(updated);
  };

  const deleteRow = (idx) => {
    const updated = data.filter((_, i) => i !== idx);
    setData(updated);
    onDataChange(updated);
  };

  return (
    <div className="" ref={wrapperRef}>
      {/* 헤더 */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-body-bold">자재 현황</h3>

        {/* <button
          onClick={addRow}
          className="btn-basic-sm"
        >
          + 행 추가
        </button> */}
      </div>

      {/* 테이블 */}
      <div className="">
        <table className="min-w-full text-sm border border-black table-fixed">
          <colgroup>
            <col style={{ width: "60px" }} /> {/* 순서 */}
            <col style={{ width: "23%" }} /> {/* 자재명 */}
            <col style={{ width: "18%" }} /> {/* 규격 */}
            <col style={{ width: "10%" }} /> {/* 단위 */}
            <col style={{ width: "10%" }} /> {/* 전일누계 */}
            <col style={{ width: "12%" }} /> {/* 금일자재 */}
            <col style={{ width: "12%" }} /> {/* 누계자재 */}
            <col style={{ width: "60px" }} /> {/* 삭제 */}
          </colgroup>

          <thead className="doc-thead">
            <tr>
              <th className="doc-th">순서</th>
              <th className="doc-th">자재명</th>
              <th className="doc-th">규격</th>
              <th className="doc-th">단위</th>
              <th className="doc-th">전일누계</th>
              <th className="doc-th">금일자재</th>
              <th className="doc-th">누계자재</th>
              <th className="w-[60px]"></th>
            </tr>
          </thead>

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={data.map((row, index) => getRowId(row, index))}
              strategy={verticalListSortingStrategy}
            >
              <tbody>
                {data.map((item, idx) => {
                  const total = item.prev_total + (item.today || 0);
                  const rowId = getRowId(item, idx);

                  return (
                    <SortableMaterialRow
                      key={rowId}
                      rowId={rowId}
                      item={item}
                      idx={idx}
                      total={total}
                      allMaterials={allMaterials}
                      handleChange={handleChange}
                      handleSelect={handleSelect}
                      deleteRow={deleteRow}
                    />
                  );
                })}
              </tbody>
            </SortableContext>
          </DndContext>
        </table>
      </div>
      <div className="flex justify-end">
        <button onClick={addRow} className="btn-basic-sm">
          + 행 추가
        </button>
      </div>
    </div>
  );
}
